/* ••[1]••••••••••••••••••••••••• EventCalendarListController.ts •••••••••••••••••••••••••••••• */

/* ••[2]•••••••••• Components ••••••••••••••• */
import "../components/event-list/index";
import { CCCEventCalendar } from "../components/calendar/index";
import { CCCEventList } from "../components/event-list/index";
import { CCCSelectSingle } from "../components/forms/select-single";
import { CCCPanel } from "../components/panel/panel";

/* ••[2]•••••••••• Interfaces ••••••••••••••• */
import { EventSourceError, FetchInfo } from "../core/interfaces/FullCalendar";
import Event, { EventPaginated, EventRSVP } from "../core/interfaces/Event";
import SelectItem from "../core/interfaces/SelectItem";
import { PageDirection, SortOption } from "../core/interfaces/Pagination";
import { VanillaRSVP } from "../core/interfaces/VanillaApi";

/* ••[2]•••••••••• Models ••••••••••••••• */
import EventModel from "../models/EventModel";

/* ••[2]•••••••••• Controllers ••••••••••••••• */
import VideoThumbnailController from "./VideoThumbnailController";

export default class EventCalendarListController {
  private $calendar: CCCEventCalendar = null;
  private $eventList: CCCEventList = null;
  private $panel: CCCPanel;
  private videoThumbnailController = new VideoThumbnailController();

  constructor(calendarAnchor: string, listAnchor: string, panelAnchor: string) {
    try {
      this.$calendar = document.querySelector(calendarAnchor);
      this.$panel = document.querySelector(panelAnchor);
      this.$eventList = document.querySelector(listAnchor);
      if (!this.$calendar || !this.$panel || !this.$eventList) {
        throw `Event Calendar List - ${calendarAnchor}, ${panelAnchor} or ${listAnchor} not found in DOM`;
      }

      this.$panel.style.display = "none";
    } catch (error) {
      console.error(error);
    }
  }

  public async init(): Promise<void> {
    if (this.$panel && this.$eventList) {
      if (await EventModel.isEventsCategoryPage()) {
        this.$panel.style.display = "block";
        this.$eventList.display(true);
        this.setupComponents();
      } else {
        this.$eventList.remove();
        this.$panel.remove();
      }
    }
  }

  private setupComponents(): void {
    this.setupCalendar();
    this.setupEventList();
    this.$calendar.init();
    this.$eventList.init();
  }

  private setupCalendar(): void {
    this.$calendar.onFetchEvents(
      (
        fetchInfo: FetchInfo,
        successCallback: (events: Array<Event>) => void,
        failureCallback: (error: EventSourceError) => void,
        currentDate: Date
      ) => {
        this.$calendar.setLoading(true);
        setTimeout(() => {
          const dateStart: string = fetchInfo.startStr.slice(0, 10);
          const dateEnd: string = fetchInfo.endStr.slice(0, 10);
          EventModel.getEventsCached(
            dateStart,
            dateEnd,
            currentDate.getFullYear().toString() +
              "-" +
              currentDate.getMonth().toString(),
            SortOption.asc,
            500,
            1
          )
            .then((result: EventPaginated): void => {
              successCallback(result.events);
              this.$calendar.setLoading(false);
              this.$calendar.render();
            })
            .catch((error) => {
              failureCallback(error);
            });
        }, 200); // fake 200ms loading time
      }
    );
    this.$calendar.onDateSelected((date: string) => {
      if (this.$eventList) {
        this.$eventList.filterByDate(date);
      }
    });
  }

  private setupEventList(): void {
    this.$eventList.onFetchEvents(
      (
        startDate: string,
        endDate: string,
        myEvents: boolean,
        sort = SortOption.asc,
        pageSize = -1,
        page = -1
      ) => {
        this.$eventList.setLoading(true);
        setTimeout(() => {
          EventModel.getEventsCached(
            startDate,
            endDate,
            startDate + "-" + endDate + (page > 0 ? "-" + page.toString() : ""),
            sort,
            pageSize,
            page
          )
            .then((result: Array<Event> | EventPaginated) => {
              this.$eventList.setLoading(false);
              if ("pagination" in result) {
                const nextPage = result.pagination.has(PageDirection.next)
                  ? Number(result.pagination.get(PageDirection.next).page)
                  : page + 1;
                if (this.$eventList.getCurrentPage() !== nextPage - 1) {
                  this.$eventList.hasMore(result.events);
                } else {
                  this.$eventList.setPagination(result.pagination);
                  this.$eventList.setEvents(result.events, myEvents);
                  this.videoThumbnailController.setUpVideoThumbnails(
                    result.events,
                    "#EventList"
                  );
                }
              } else {
                this.$eventList.setEvents(result, myEvents);
                this.videoThumbnailController.setUpVideoThumbnails(
                  result,
                  "#EventList"
                );
              }
            })
            .catch((error) => {
              console.error(error);
            });
        }, 200); // fake 200ms loading time
      }
    );
    this.$eventList.onRSVP(
      (eventID: number, select: CCCSelectSingle, value: SelectItem) => {
        select.setLoading(true);
        let rsvpOption: string;
        switch (value.name) {
          case EventRSVP.Going:
            rsvpOption = VanillaRSVP.Going;
            break;
          case EventRSVP.NotGoing:
            rsvpOption = VanillaRSVP.NotGoing;
            break;
          case EventRSVP.Maybe:
            rsvpOption = VanillaRSVP.Maybe;
            break;
          default:
            rsvpOption = value.name;
            break;
        }
        EventModel.rsvpEvent(rsvpOption, eventID)
          .then((success) => {
            if (!success) {
              select.displayPlaceholder();
            }
            select.setLoading(false);
          })
          .catch((error) => {
            console.log(error);
          });
      }
    );
  }
}
